/* eslint-disable max-classes-per-file */
import { addBreadcrumb, captureException } from '@sentry/browser';
import { htmlDecode, debounce, getCartApiErrorMessage } from './helpers/utils.js';
import { fetchConfig } from './helpers/fetch';
import { loadProductAccessories, addEventToLoadAccessoriesMobile } from './helpers/product-accessories.js';
import { calculateAvailabilityAndLeadTime } from './helpers/availability-lead-time.js';

const getCartDomDebugInfo = () => ({
  pathname: window.location.pathname,
  readyState: document.readyState,
  activeElement: document.activeElement?.tagName,
  hasCartItems: Boolean(document.getElementById('main-cart-items')),
  hasCartSummary: Boolean(document.getElementById('main-cart-summary')),
  hasCartActions: Boolean(document.getElementById('main-cart-actions')),
  hasCartFooter: Boolean(document.getElementById('main-cart-footer')),
  hasCartErrors: Boolean(document.getElementById('cart-errors')),
  hasLineItemStatus: Boolean(document.getElementById('shopping-cart-line-item-status')),
  cartItemsCount: document.querySelectorAll('[id^="CartItem-"]').length,
  quantityInputsCount: document.querySelectorAll('[id^="Quantity-"]').length,
});

const getSectionDebugInfo = (section, parsedState) => {
  const container = document.getElementById(section.id);
  const sectionHtml = parsedState?.sections?.[section.section];
  const parsedSection = sectionHtml ? new DOMParser().parseFromString(sectionHtml, 'text/html') : null;

  return {
    section,
    containerExists: Boolean(container),
    containerHasSelector: Boolean(container?.querySelector(section.selector)),
    responseSectionExists: typeof sectionHtml === 'string',
    responseSectionHasSelector: Boolean(parsedSection?.querySelector(section.selector)),
    responseSectionLength: sectionHtml?.length,
    responseSectionSnippet: sectionHtml?.slice(0, 500),
    responseSectionKeys: parsedState?.sections ? Object.keys(parsedState.sections) : [],
    itemCount: parsedState?.item_count,
    dom: getCartDomDebugInfo(),
  };
};

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.handleClick = this.handleClick.bind(this);
  }

  connectedCallback() {
    this.abortController = new AbortController();

    this.addEventListener('click', this.handleClick, { signal: this.abortController.signal });
  }

  disconnectedCallback() {
    this.abortController.abort();
  }

  handleClick(event) {
    event.preventDefault();

    this.closest('cart-items').updateQuantity(this.dataset.index, 0);
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();

    this.lineItemStatusElement = document.getElementById('shopping-cart-line-item-status');

    this.currentItemCount = Array.from(this.querySelectorAll('[name="updates[]"]')).reduce(
      (total, quantityInput) => total + parseInt(quantityInput.value, 10),
      0
    );

    this.debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, 300);

    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.preventQuantityReduceToZero = this.preventQuantityReduceToZero.bind(this);
    this.handleChange = this.handleChangeEvent.bind(this);
  }

  handleChangeEvent(event) {
    const countryEl = this.querySelector('[data-cart-country]');

    if (countryEl && event.target === countryEl) {
      this.onChange(event);
    } else {
      this.debouncedOnChange(event);
    }
  }

  connectedCallback() {
    this.abortController = new AbortController();

    this.addEventListener('keydown', this.handleKeyDown, { signal: this.abortController.signal });
    this.addEventListener('change', this.handleChange, { signal: this.abortController.signal });
    this.addEventListener('change', this.preventQuantityReduceToZero, { signal: this.abortController.signal });
    this.cartSubtotalReCalc();
  }

  disconnectedCallback() {
    this.abortController.abort();
  }

  handleKeyDown(event) {
    if (event.key === 'Enter' && event.target.classList.contains('quantity__input')) {
      event.preventDefault();

      if (document.activeElement === event.target) {
        event.target.blur();
      }
    }
  }

  preventQuantityReduceToZero(event) {
    if (event.target.classList.contains('quantity__input')) {
      const buttonMinusEl = event.target.parentElement.querySelector('.quantity__button[name=minus]');

      if (buttonMinusEl) {
        buttonMinusEl.querySelectorAll('span')[1].classList.toggle('hidden', event.target.value <= 1);
        buttonMinusEl.disabled = event.target.value <= 1;
      }
    }
  }

  cartSubtotalReCalc() {
    const shopifyShippingRate = Shopify.currency.rate;

    const cartSubtotalContainer = document.getElementById('main-cart-summary');

    if (!cartSubtotalContainer) {
      captureException(new Error('Cart instrumentation: missing main-cart-summary in cartSubtotalReCalc'), {
        extra: getCartDomDebugInfo(),
      });

      return;
    }

    const cartSubtotal = cartSubtotalContainer.querySelector('[data-subtotal]');

    if (!cartSubtotal) {
      captureException(new Error('Cart instrumentation: missing data-subtotal in cartSubtotalReCalc'), {
        extra: getCartDomDebugInfo(),
      });

      return;
    }

    if (shopifyShippingRate !== '1.0') {
      const cartShipping = cartSubtotalContainer.querySelector('[data-shipping]');
      const cartTotals = cartSubtotalContainer.querySelector('[data-totals]');
      const vatValue = Number(cartSubtotalContainer.querySelector('[data-vat]')?.dataset.vat);
      const cartShippingVat = cartSubtotalContainer.querySelector('[data-shipping-vat]');
      const cartTotalsVat = cartSubtotalContainer.querySelector('[data-totals-vat]');

      if (!cartShipping || !cartTotals || (vatValue && (!cartShippingVat || !cartTotalsVat))) {
        captureException(new Error('Cart instrumentation: missing subtotal recalculation elements'), {
          extra: {
            ...getCartDomDebugInfo(),
            hasCartShipping: Boolean(cartShipping),
            hasCartTotals: Boolean(cartTotals),
            hasVatValue: Boolean(vatValue),
            hasCartShippingVat: Boolean(cartShippingVat),
            hasCartTotalsVat: Boolean(cartTotalsVat),
          },
        });

        return;
      }

      const options = { minimumFractionDigits: 2, maximumFractionDigits: 2 };
      const sumRecalc = (sum) => Number(((sum * 1) / 100).toFixed(2));

      const freeShippingLimit =
        cartShipping.dataset.freeShipping !== 'false' && sumRecalc(cartShipping.dataset.freeShipping);

      const cartSubtotalValue = Number(cartSubtotal.dataset.subtotal) / 100;
      const zeroMoneyFormatWithCurrency = cartSubtotal.dataset.moneyFormatWithCurrency;
      let zeroMoneyFormat = cartSubtotal.dataset.moneyFormat;
      let cartShippingValue = cartShipping.dataset.shipping;

      zeroMoneyFormat =
        zeroMoneyFormatWithCurrency.includes('.00') || zeroMoneyFormatWithCurrency.includes(',00')
          ? zeroMoneyFormat
          : '0';

      if (freeShippingLimit > cartSubtotalValue || !freeShippingLimit) {
        cartShipping.innerHTML = cartShipping.innerHTML.replace(
          zeroMoneyFormat,
          sumRecalc(cartShippingValue).toLocaleString('en-US', options)
        );

        if (vatValue) {
          cartShippingVat.innerHTML = cartShippingVat.innerHTML.replace(
            zeroMoneyFormat,
            sumRecalc(cartShippingValue * vatValue).toLocaleString('en-US', options)
          );
        }
      } else {
        if (vatValue) {
          cartShipping.innerHTML = '';
          cartShippingVat.innerHTML = theme.locales.freeShipping;
        } else {
          cartShipping.innerHTML = theme.locales.freeShipping;
        }

        cartShippingValue = 0;
      }

      const cartTotalsReCalc = (sumRecalc(cartShippingValue) + cartSubtotalValue).toLocaleString('en-US', options);

      cartTotals.innerHTML = cartTotals.innerHTML.replace(zeroMoneyFormat, cartTotalsReCalc);

      if (vatValue) {
        const cartTotalsReCalcVat = (
          sumRecalc(cartShippingValue * vatValue) +
          cartSubtotalValue * vatValue
        ).toLocaleString('en-US', options);

        cartTotalsVat.innerHTML = cartTotalsVat.innerHTML.replace(zeroMoneyFormat, cartTotalsReCalcVat);
      }
    }

    if (cartSubtotal) {
      this.checkOrderMinimumValue(cartSubtotal.dataset.subtotal);
    }
  }

  checkOrderMinimumValue(cartSubtotalValue) {
    // Shopify.currency.rate converts out of the base currency, so a currency without
    // its own setting falls back to the base amount.
    const { minimumOrderAmount, initCurrency } = window.theme;
    const explicitAmount = minimumOrderAmount[Shopify.currency.active];
    const usesLiveRate = !explicitAmount;
    const configuredAmount = usesLiveRate ? minimumOrderAmount[initCurrency] : explicitAmount;

    if (!configuredAmount) {
      return;
    }

    // Numeric, not formatMoney: its locale string ("1,250.00") coerces to NaN below.
    const baseValue = parseFloat(String(configuredAmount).replace(/,/g, '.'));
    const minOrderValue = usesLiveRate ? baseValue * parseFloat(Shopify.currency.rate) : baseValue;

    if (minOrderValue > 0) {
      // Compare in cents: a cart exactly on the minimum may check out, and a converted
      // minimum such as 17.601 must not block the 17.60 cart it is displayed as.
      if (Number(cartSubtotalValue) < Math.round(minOrderValue * 100)) {
        document.querySelectorAll('[data-checkout-button]').forEach((button) => {
          button.disabled = true;
        });
        document.getElementById('cart-errors').innerHTML = htmlDecode(window.cartStrings.minimumOrderAmountNotice);
        const currencySymbol = window.theme.currencySymbol.split('{')[0];
        const { currency } = window.theme.cartState;
        const tresholdEl = document.querySelector('[data-cart-order-treshold]');

        if (tresholdEl) {
          tresholdEl.innerText = htmlDecode(`${currencySymbol}${minOrderValue.toFixed(2)}\xa0${currency}`);
        }

        // Show the incl-VAT amount alongside the ex-VAT threshold where VAT applies.
        const tresholdInclEl = document.querySelector('[data-cart-order-treshold-incl]');
        const vatRate = window.theme.currentVATRate;

        if (tresholdInclEl && typeof vatRate === 'number') {
          const minOrderValueInclVat = minOrderValue * vatRate;

          tresholdInclEl.innerText = htmlDecode(`${currencySymbol}${minOrderValueInclVat.toFixed(2)}\xa0${currency}`);
        }
      } else {
        document.getElementById('cart-errors').innerHTML = '';
        // Release only the buttons this check disabled; an empty cart or a
        // quote-only item marks them data-checkout-blocked.
        document
          .querySelectorAll('[data-checkout-button]:not([data-checkout-blocked])')
          .forEach((button) => button.removeAttribute('disabled'));
      }
    }
  }

  onShippingCountryChange(countryEl) {
    const localizationForm = document.querySelector('form.localization-form');

    if (localizationForm) {
      const formInput = localizationForm.querySelector('[name="country_code"]');

      if (formInput) {
        formInput.value = countryEl.value;
        localizationForm.submit();
      }
    }
  }

  onChange(event) {
    const countryEl = this.querySelector('[data-cart-country]');

    if (countryEl && event.target === countryEl) {
      this.onShippingCountryChange(countryEl);
    } else {
      // don't run when customer changes VAT id input or any bulk-add input (SKU rows, CSV file)
      if (
        event.target.id === 'VatId' ||
        event.target.classList.contains('bulk-add-products-input') ||
        event.target.closest('bulk-add-products')
      ) {
        return;
      }

      this.updateQuantity(event.target.dataset.index, event.target.value, document.activeElement.getAttribute('name'));
    }
  }

  getSectionsToRender() {
    // The accessories block is loaded dynamically and may be absent from the DOM; sections
    // whose container or section id is missing cannot be requested nor rendered.
    return [
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '#shopify-section-cart-icon-bubble',
      },
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items')?.dataset.id,
        selector: '.js-cart-item-contents',
      },
      {
        id: 'main-cart-summary',
        section: document.getElementById('main-cart-summary')?.dataset.id,
        selector: '.js-cart-summary-contents',
      },
      {
        id: 'main-cart-actions',
        section: document.getElementById('main-cart-actions')?.dataset.id,
        selector: '.js-cart-actions-contents',
      },
      {
        id: 'accessories',
        section: document.getElementById('accessories')?.dataset.id,
        selector: '#accessories',
      },
    ].filter((section) => section.section && document.getElementById(section.id));
  }

  updateQuantity(line, quantity, name) {
    addBreadcrumb({
      category: 'cart',
      message: 'cart.updateQuantity called',
      level: 'info',
      data: {
        line,
        quantity,
        name,
        dom: getCartDomDebugInfo(),
      },
    });

    this.enableLoading(line);

    const options = JSON.parse(document.getElementById(`Quantity-${line}`).dataset.options ?? '{}');
    const { leadTimeArray, inventoryQuantity, inventoryPolicy, useUp } = options;

    const { availabilityStatus, leadTime } = calculateAvailabilityAndLeadTime(
      leadTimeArray,
      inventoryQuantity,
      inventoryPolicy,
      quantity,
      useUp
    );

    const body = JSON.stringify({
      line,
      quantity,
      sections: [...new Set(this.getSectionsToRender().map((section) => section.section))],
      sections_url: window.location.pathname,
      lead_time: leadTime,
      availability: availabilityStatus,
    });

    fetch(`${window.routes.cart_change_url}`, { ...fetchConfig(), body, signal: this.abortController.signal })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        addBreadcrumb({
          category: 'cart',
          message: 'cart.change response parsed',
          level: 'info',
          data: {
            itemCount: parsedState.item_count,
            responseSectionKeys: parsedState.sections ? Object.keys(parsedState.sections) : [],
          },
        });

        // The Cart API returns an error payload without sections when the change is rejected
        // (e.g. requested quantity exceeds stock); surface the message instead of rendering.
        if (!parsedState.sections) {
          document.getElementById('cart-errors').textContent = getCartApiErrorMessage(
            parsedState,
            window.cartStrings.error
          );

          return;
        }

        // Clear any error left by a previously rejected change; cartSubtotalReCalc
        // re-populates it below when the minimum order amount is not met.
        document.getElementById('cart-errors').textContent = '';

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        document.getElementById('main-cart-footer')?.classList.toggle('is-empty', parsedState.item_count === 0);
        this.getSectionsToRender().forEach((section) => {
          try {
            const container = document.getElementById(section.id);
            const sectionHtml = parsedState.sections[section.section];

            // The Section Rendering API only returns sections rendered on the sections_url
            // page; skip sections missing from the response or removed from the DOM meanwhile.
            if (!container || typeof sectionHtml !== 'string') {
              return;
            }

            const elementToReplace = container.querySelector(section.selector) || container;
            const newInnerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);

            // The response section may render empty (e.g. accessories without matches)
            if (newInnerHTML === null) {
              return;
            }

            elementToReplace.innerHTML = newInnerHTML;
          } catch (e) {
            captureException(e, {
              extra: {
                ...getSectionDebugInfo(section, parsedState),
                sectionsToRender: this.getSectionsToRender().map((sections) => sections.section),
              },
            });
          }
        });
        this.cartSubtotalReCalc();
        loadProductAccessories(this.abortController.signal);
        addEventToLoadAccessoriesMobile(this.abortController.signal);
        this.updateLiveRegions(line, parsedState.item_count);
        document.getElementById(`CartItem-${line}`)?.querySelector(`[name="${name}"]`)?.focus();
      })
      .catch((e) => {
        if (e.name === 'AbortError') {
          return;
        }

        document.querySelector('.loading-overlay').classList.add('hidden');
        document.getElementById('cart-errors').textContent = window.cartStrings.error;
        // if it returns error disable checkout buttons
        document.querySelectorAll('[data-checkout-button]').forEach((button) => {
          button.disabled = true;
        });
        document.querySelector('.cart__delivery-estimation').classList.add('visually-hidden');
        captureException(e);
      })
      .finally(() => {
        this.disableLoading();
      });
  }

  getHighestLeadTime(data) {
    let highestExtraLeadTime = 0;

    data.forEach((item) => {
      const itemOptions = JSON.parse(item.dataset.options);

      if (itemOptions.customerLeadTime > highestExtraLeadTime && item.value > itemOptions.inventoryQuantity) {
        highestExtraLeadTime = itemOptions.customerLeadTime;
      }

      if (itemOptions.isValidArrayLeadTime) {
        let prev_max_qty = 1;

        itemOptions.leadTimeArray.forEach((leadTimeData) => {
          if (prev_max_qty <= item.value && item.value <= leadTimeData.max_qty) {
            highestExtraLeadTime = leadTimeData.lead_time;
          }

          prev_max_qty = leadTimeData.max_qty + 1;
        });
      }
    });

    return highestExtraLeadTime;
  }

  findSoldOutProduct(data) {
    let findSoldOutProduct = false;

    data.forEach((item) => {
      const itemOptions = JSON.parse(item.dataset.options);
      const itemValue = item.value;

      if (itemValue > itemOptions.inventoryQuantity) {
        findSoldOutProduct = true;
      }
    });

    return findSoldOutProduct;
  }

  // show error texts, should not be deleted.
  updateLiveRegions(line, itemCount) {
    if (this.currentItemCount === itemCount) {
      const lineItemError = document.getElementById(`Line-item-error-${line}`);
      const lineItemErrorText = lineItemError?.querySelector('.cart-item__error-text');
      const quantityInput = document.getElementById(`Quantity-${line}`);

      if (lineItemErrorText && quantityInput) {
        lineItemErrorText.innerHTML = window.cartStrings.quantityError.replace('[quantity]', quantityInput.value);
      } else {
        captureException(new Error('Cart instrumentation: missing live region quantity error elements'), {
          extra: {
            line,
            itemCount,
            currentItemCount: this.currentItemCount,
            hasLineItemError: Boolean(lineItemError),
            hasLineItemErrorText: Boolean(lineItemErrorText),
            hasQuantityInput: Boolean(quantityInput),
            dom: getCartDomDebugInfo(),
          },
        });
      }
    }

    this.currentItemCount = itemCount;

    if (!this.lineItemStatusElement) {
      captureException(new Error('Cart instrumentation: missing shopping-cart-line-item-status in updateLiveRegions'), {
        extra: {
          line,
          itemCount,
          dom: getCartDomDebugInfo(),
        },
      });

      return;
    }

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus = document.getElementById('cart-live-region-text');

    if (!cartStatus) {
      captureException(new Error('Cart instrumentation: missing cart-live-region-text in updateLiveRegions'), {
        extra: {
          line,
          itemCount,
          dom: getCartDomDebugInfo(),
        },
      });

      return;
    }

    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML ?? null;
  }

  enableLoading(_line) {
    document.getElementById('main-cart-items').classList.add('cart__items--disabled');
    document.querySelector('.loading-overlay').classList.remove('hidden');
    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading() {
    document.querySelector('.loading-overlay').classList.add('hidden');
    document.getElementById('main-cart-items').classList.remove('cart__items--disabled');
  }

  updateCheckOutButtons(hidden, disabled, blocked) {
    const realFirstCheckoutButton = document.querySelectorAll('.cart__ctas')[0];

    realFirstCheckoutButton.classList.toggle('button--hidden', hidden);

    // The buttons are not re-rendered here, so carry over the fresh render's marker
    // before checkOrderMinimumValue decides which ones it may re-enable.
    document.querySelectorAll('[data-checkout-button]').forEach((button) => {
      button.toggleAttribute('data-checkout-blocked', blocked);
    });

    if (disabled) {
      document.querySelectorAll('[data-checkout-button]').forEach((button) => {
        button.disabled = true;
      });
      document.querySelector('.cart__delivery-estimation').classList.add('visually-hidden');
    } else {
      document.querySelectorAll('[data-checkout-button]').forEach((button) => button.removeAttribute('disabled'));
      document.querySelector('.cart__delivery-estimation').classList.remove('visually-hidden');
    }

    const cartSubtotal = document.querySelector('[data-subtotal]');

    if (cartSubtotal) {
      this.checkOrderMinimumValue(cartSubtotal.dataset.subtotal);
    }
  }
}
customElements.define('cart-items', CartItems);

try {
  if (!localStorage.getItem('currencyChanged')) {
    localStorage.removeItem('checkoutOpened');
  }
} catch {
  // Storage access can be denied entirely (privacy settings, sandboxed contexts)
}
